import React from "react";
import InfiniteSearchResults from "./Components/InfiniteSearchResults/index";
import {
  InstantSearch,
  Stats,
  Configure,
  CurrentRefinements,
  ClearRefinements
} from "react-instantsearch-dom";

import SortBy from "./Components/SortBy";
import CruiseFilters from "./Components/CruiseFilters";
import CruiseCardResult from "./Components/CruiseCardResult";
import "./FindYourCruise.scss";
import { getStartDate } from "components/MonthCalendar/utils";

import { t, Trans, plural } from "@lingui/macro";
import { ComponentBackgroundColorWithDestination } from "types/componentBackground";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import classNames from "classnames";
import DateOnlyFormat from "components/DateOnlyFormat";
import { FYCQuery } from "./FYCQueryBuilder";
import { i18n } from "@lingui/core";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

export interface FindYourCruiseProps {
  searchClient;
  initialSearchQuery: FYCQuery;
  resultsPageSize: number;
  defaultSearchIndexName: string;
  searchState;
  onSearchStateChange;
  createURL;
  backgroundColor: ComponentBackgroundColorWithDestination | "neutral10";
  showTitle?: boolean;
  title?: string | JSX.Element;
  titleAlign?: "left" | "center";
}

export interface FindYourCruiseFiltersProps {
  showFreeTextSearch?: boolean;
  showExpeditionFilter: boolean;
  specialTypeFilter: string | undefined;
  showShipFilter: boolean;
  showDestinationFilter: boolean;
  showDurationFilter: boolean;
  showOthersFilter: boolean;
  showPromosFilter: boolean;
}

const departureYearMonthTransformer = (item) => {
  const transformedItems = item.items.map((value) => {
    return {
      ...value,
      label: (
        <DateOnlyFormat
          value={getStartDate(value.label)}
          format={{
            month: "short",
            year: "numeric"
          }}
        />
      )
    };
  });

  return { ...item, items: transformedItems };
};

const refinementListLabels = {
  "content.shipName": t`Ship`,
  departureYearMonth: t`Departure Date`,
  dayGroup: t`Duration`,
  destinationName: t`Destination`,
  promos: t`Offers`
};

const refinementListTransformer = {
  departureYearMonth: departureYearMonthTransformer
};

const transformActiveFiltersLabel = () => (items) => {
  return items.map((item) => {
    let label;
    const transformer = refinementListTransformer[item.attribute];
    const refinementLabel = refinementListLabels[item.attribute];
    const overrideLabel = refinementLabel ? i18n._(refinementLabel) : null;
    const transformedItem = transformer ? transformer(item) : item;

    if (item.id == "query") label = `Keywords: ${item.currentRefinement}`;
    else {
      label = overrideLabel ? `${overrideLabel}: ` : item.label;
    }

    return {
      ...transformedItem,
      label: label
    };
  });
};

const FindYourCruise: React.FC<
  FindYourCruiseProps & FindYourCruiseFiltersProps
> = ({
  searchClient,
  initialSearchQuery,
  resultsPageSize,
  defaultSearchIndexName,
  searchState,
  onSearchStateChange,
  createURL,
  backgroundColor,
  showTitle = true,
  title = undefined,
  titleAlign = "center",
  ...filtersProps
}) => {
  const geoLocation = useGeolocation();
  const currency = geoLocation.defaultCurrency?.toLowerCase() || "usd";

  return (
    <InstantSearch
      indexName={defaultSearchIndexName}
      searchClient={searchClient}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
    >
      <Configure
        filters={initialSearchQuery}
        hitsPerPage={resultsPageSize}
        analytics={process.env.GATSBY_ALGOLIA_ENABLE_ANALYTICS}
        //enablePersonalization={true}
        distinct
      />

      <div
        className={`ssc-find-cruise ssc-find-cruise__background--${backgroundColor}`}
        data-content-name="find_your_cruise"
      >
        <SectionContainer backgroundColor="transparent">
          {showTitle && title && (
            <h2
              className={classNames([
                "ssc-find-cruise__title",
                titleAlign == "center"
                  ? "ssc-find-cruise__title--center"
                  : "ssc-find-cruise__title--left"
              ])}
            >
              {title}
            </h2>
          )}
          <div className="ssc-find-cruise__filters-container">
            <div className="ssc-find-cruise__filters">
              <CruiseFilters {...filtersProps} />
            </div>
          </div>
        </SectionContainer>
        <div
          className="ssc-find-cruise__results-container"
          data-content-name="cruise_search_results"
        >
          <SectionContainer backgroundColor="transparent">
            <div className="ssc-find-cruise__result-stats-container">
              <div className="ssc-find-cruise__result-stats-container--sort">
                <div className="ssc-find-cruise__result-stats-container--left">
                  <Stats
                    translations={{
                      stats(nbHits, _timeSpentMS) {
                        return plural(nbHits, {
                          one: "# Cruise Found",
                          other: "# Cruises Found",
                          zero: "No Cruises Found"
                        });
                      }
                    }}
                  />
                </div>
                <div className="ssc-find-cruise__result-stats-container--right">
                  <div className="ssc-find-cruise__sort-by-container">
                    <label htmlFor="fyc-sort-by">
                      <Trans>Sort By</Trans>
                    </label>
                    <SortBy
                      id="fyc-sort-by"
                      defaultRefinement={defaultSearchIndexName}
                      items={[
                        {
                          value: defaultSearchIndexName,
                          label: t`Departure Date`
                        },
                        {
                          value: `${defaultSearchIndexName}__price-from-${currency}-asc`,
                          label: t`Price (Low to High)`
                        },
                        {
                          value: `${defaultSearchIndexName}__price-from-${currency}-desc`,
                          label: t`Price (High to Low)`
                        },
                        {
                          value: `${defaultSearchIndexName}__duration-asc`,
                          label: t`Duration (Min to Max)`
                        },
                        {
                          value: `${defaultSearchIndexName}__duration-desc`,
                          label: t`Duration (Max to Min)`
                        }
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div className="ssc-find-cruise__result__refinements">
                <ClearRefinements
                  translations={{
                    reset: t`Clear all filters`
                  }}
                />
                <CurrentRefinements
                  clearsQuery
                  transformItems={transformActiveFiltersLabel()}
                />
              </div>
            </div>
            <InfiniteSearchResults
              hitComponent={CruiseCardResult}
              hitComponentProps={{
                showDestination: filtersProps.showDestinationFilter
              }}
            />
          </SectionContainer>
        </div>
      </div>
    </InstantSearch>
  );
};

export default React.memo(FindYourCruise);
